/**
 * Reddit Advertising — Sync Reports Module (Story 19.11, Task 1)
 *
 * Reddit Ads API v3 reports endpoint:
 *   POST https://ads-api.reddit.com/api/v3/ad_accounts/{ad_account_id}/reports
 *
 * Body shape (the outer `{"data": {...}}` wrapper is mandatory in v3):
 *   {
 *     "data": {
 *       "starts_at": "YYYY-MM-DDTHH:00:00Z",
 *       "ends_at":   "YYYY-MM-DDTHH:00:00Z",
 *       "fields":    ["IMPRESSIONS", "CLICKS", ...],
 *       "breakdowns":["DATE", "CAMPAIGN_ID"],
 *       "time_zone_id": "America/Los_Angeles"
 *     }
 *   }
 *
 * Key v3 invariants enforced here:
 *   - Max 3 breakdowns per request (4 ONLY when both COUNTRY+REGION present)
 *   - HOUR breakdown gated to ranges ≤ 7 days (defensive — Reddit's row-cap bites)
 *   - REACH/FREQUENCY clamp to 2024-06-01 floor with warning
 *   - Deprecated wire fields stripped at request build
 *     (CLICK_ATTRIBUTION_WINDOW / VIEW_ATTRIBUTION_WINDOW / GALLERY_ITEM_CAPTION
 *     as fields — they are now DIMENSIONS in v3)
 *   - GALLERY_ITEM_ID breakdown strips conversion fields (delivery-only scope)
 *   - Aliases AD_GROUP_ID/ADGROUP_ID and AD_ACCOUNT_ID/ACCOUNT_ID normalized
 *   - Half-open interval chunking [start, end) for the async client-side fan-out
 *     so adjacent chunks don't double-count boundary days
 *
 * NOTE: Reddit's v3 reports endpoint is synchronous-by-design. There is NO
 * report_run_id polling protocol. Our "async" path in `analytics.ts` is purely
 * client-side temporal partitioning — the request shape is identical.
 */

import { REDDIT_ADS_API_BASE } from "./api";

// ============================================================================
// Constants — single source of truth for the per-request guards
// ============================================================================

/** Per-request URL builder: hardens against malformed ad-account IDs. */
export const REDDIT_ADS_REPORTS_ENDPOINT = (adAccountId: string): string =>
  `${REDDIT_ADS_API_BASE}/ad_accounts/${encodeURIComponent(adAccountId)}/reports`;

/** Per-request timeout. Reddit reports queries can hit 10-15s server-side. */
export const REDDIT_REPORTS_TIMEOUT_MS = 30_000;

/** Maximum breakdowns per single sync request. */
export const REDDIT_ADS_MAX_BREAKDOWNS_PER_REQUEST = 3 as const;

/** Special-case allowance for the COUNTRY+REGION combined breakdown. */
export const REDDIT_ADS_MAX_BREAKDOWNS_WITH_COUNTRY_REGION = 4 as const;

/** Global per-fetchMetrics-call cap (peak Worker memory). */
export const REDDIT_ADS_MAX_FETCHMETRICS_DAYS = 90 as const;

/** Defensive cap on HOUR-breakdown date ranges (Reddit's row-cap bites first). */
export const REDDIT_ADS_MAX_HOUR_BREAKDOWN_DAYS = 7 as const;

/** Chunk size for client-side temporal partitioning ("async" fallback). */
export const REDDIT_ADS_MAX_ASYNC_CHUNK_DAYS = 30 as const;

/** Parallel chunk-fan-out cap (under Reddit's 60 req/min token quota). */
export const REDDIT_ADS_MAX_PARALLEL_CHUNKS = 6 as const;

/** Top-N defensive cap on subreddit rows (KV 25MB ceiling). */
export const REDDIT_ADS_MAX_SUBREDDITS_PER_REPORT = 200 as const;

/**
 * REACH and FREQUENCY metrics are only available from June 1, 2024 forward.
 * Adapter clamps `starts_at` to this date IFF the fields list includes either,
 * and emits a `warnings[]` entry.
 */
export const REDDIT_ADS_REACH_FREQ_AVAILABLE_FROM = new Date(
  "2024-06-01T00:00:00Z"
);

/**
 * Wire-format fields that Reddit silently returns no data for. The adapter
 * strips these from the `fields[]` list at request-build time with a warning,
 * preventing developers from accidentally including them. These three are now
 * DIMENSIONS in v3 — they should appear in `breakdowns`, not `fields`.
 */
export const REDDIT_ADS_DEPRECATED_METRIC_FIELDS = [
  "CLICK_ATTRIBUTION_WINDOW",
  "VIEW_ATTRIBUTION_WINDOW",
  "GALLERY_ITEM_CAPTION",
] as const;

/** Breakdowns that Reddit returns only delivery metrics for (no conversions). */
export const REDDIT_ADS_DELIVERY_ONLY_DIMENSIONS = ["GALLERY_ITEM_ID"] as const;

// ============================================================================
// Types
// ============================================================================

/**
 * The 6 sync report types orchestrated by the default fan-out (camelCase
 * preserves Reddit's "Ad Group" vocabulary in the discriminator).
 */
export type RedditAdsReportType =
  | "overview"
  | "campaigns"
  | "adGroups"
  | "ads"
  | "subreddits"
  | "countryRegion";

/**
 * Closed-set string-literal union of the 21 breakdown values from the catalogue
 * (rows 1-21 in `_bmad/bmm/data/integration-metrics/reddit-ads.md`).
 *
 * Aliases (`ADGROUP_ID`, `ACCOUNT_ID`) are accepted at the build step but
 * normalized to canonical (`AD_GROUP_ID`, `AD_ACCOUNT_ID`) before transmission.
 */
export type RedditAdsBreakdown =
  | "AD_ACCOUNT_ID"
  | "CAMPAIGN_ID"
  | "AD_GROUP_ID"
  | "AD_ID"
  | "DATE"
  | "HOUR"
  | "COUNTRY"
  | "REGION"
  | "DMA"
  | "METRO"
  | "GENDER"
  | "INTEREST"
  | "OS_TYPE"
  | "SUBREDDIT"
  | "COMMUNITY"
  | "KEYWORD"
  | "PLACEMENT"
  | "CAROUSEL_CARD"
  | "GALLERY_ITEM_ID"
  | "CLICK_ATTRIBUTION_WINDOW"
  | "VIEW_ATTRIBUTION_WINDOW";

/** Aliases accepted at request-build time but normalized away. */
export type RedditAdsBreakdownAlias = "ADGROUP_ID" | "ACCOUNT_ID";

/** Both canonical breakdowns and aliases — accepted as input, normalized on output. */
export type RedditAdsBreakdownInput =
  | RedditAdsBreakdown
  | RedditAdsBreakdownAlias;

/**
 * Computed-from-breakdowns aggregation level. Reddit v3 has NO `level=` query
 * param — aggregation is entirely controlled by which structure breakdowns are
 * in the request. This type is a derived concept exposed for the catalogue,
 * NOT a wire-format value.
 */
export type RedditAdsAggregationLevel =
  | "adAccount"
  | "campaign"
  | "adGroup"
  | "ad";

/** Click-attribution-window enum values (per Reddit v3 — replaces metric suffixes). */
export type RedditAdsClickAttributionWindow = "1d" | "7d" | "28d";

/** View-attribution-window enum values. */
export type RedditAdsViewAttributionWindow = "none" | "1d";

export interface RedditAdsReportRequestOptions {
  /** UPPERCASE Reddit field names (e.g. "IMPRESSIONS", "CONVERSION_PURCHASE_CLICKS"). */
  readonly fields: ReadonlyArray<string>;
  /** Up to 3 breakdowns (4 if COUNTRY+REGION); aliases accepted. */
  readonly breakdowns: ReadonlyArray<RedditAdsBreakdownInput>;
  readonly startsAt: Date;
  readonly endsAt: Date;
  readonly timeZoneId: string;
  readonly clickAttributionWindow?: RedditAdsClickAttributionWindow;
  readonly viewAttributionWindow?: RedditAdsViewAttributionWindow;
}

export interface RedditAdsReportRequestWarning {
  readonly reason: string;
}

export interface RedditAdsReportRequest {
  readonly url: string;
  readonly body: {
    readonly data: {
      readonly starts_at: string;
      readonly ends_at: string;
      readonly fields: ReadonlyArray<string>;
      readonly breakdowns: ReadonlyArray<RedditAdsBreakdown>;
      readonly time_zone_id: string;
      readonly click_attribution_window?: RedditAdsClickAttributionWindow;
      readonly view_attribution_window?: RedditAdsViewAttributionWindow;
    };
  };
  readonly warnings: ReadonlyArray<RedditAdsReportRequestWarning>;
}

// ============================================================================
// Helpers
// ============================================================================

/** Days between two dates (Date-arithmetic helper; ignores DST since we use UTC). */
function daysBetween(start: Date, end: Date): number {
  const startMs = Date.UTC(
    start.getUTCFullYear(),
    start.getUTCMonth(),
    start.getUTCDate()
  );
  const endMs = Date.UTC(
    end.getUTCFullYear(),
    end.getUTCMonth(),
    end.getUTCDate()
  );
  return Math.round((endMs - startMs) / (24 * 60 * 60 * 1000));
}

/**
 * Returns true if breakdowns array contains exactly the COUNTRY+REGION pair
 * (with up to 2 additional breakdowns), i.e. the v3 special-case that allows
 * 4 total breakdowns instead of the normal cap of 3.
 */
function isCountryRegionSpecialCase(
  breakdowns: ReadonlyArray<RedditAdsBreakdown>
): boolean {
  return (
    breakdowns.length <= REDDIT_ADS_MAX_BREAKDOWNS_WITH_COUNTRY_REGION &&
    breakdowns.includes("COUNTRY") &&
    breakdowns.includes("REGION")
  );
}

/** Canonicalize alias breakdowns to their wire-format form. */
function normalizeBreakdown(
  input: RedditAdsBreakdownInput
): RedditAdsBreakdown {
  if (input === "ADGROUP_ID") return "AD_GROUP_ID";
  if (input === "ACCOUNT_ID") return "AD_ACCOUNT_ID";
  return input;
}

/**
 * Encode a Date as ISO 8601 with `Z` UTC suffix at hour boundaries (no
 * milliseconds, no minutes, no seconds beyond `:00:00`).
 *
 * Reddit applies `time_zone_id` server-side to compute day boundaries from
 * these UTC timestamps — so we send UTC-midnight + the IANA timezone separately
 * rather than computing pre-shifted boundaries. (DIFFERENT from Snapchat which
 * encodes ISO 8601 with offset suffix and computes boundaries client-side.)
 */
function encodeRedditTimestamp(d: Date): string {
  const yyyy = d.getUTCFullYear().toString().padStart(4, "0");
  const mm = (d.getUTCMonth() + 1).toString().padStart(2, "0");
  const dd = d.getUTCDate().toString().padStart(2, "0");
  const hh = d.getUTCHours().toString().padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:00:00Z`;
}

/**
 * Field families that should be stripped when GALLERY_ITEM_ID is the active
 * breakdown — Reddit returns null/empty for conversions at gallery-card scope.
 */
const CONVERSION_FIELD_PREFIXES = [
  "CONVERSION_",
  "KEY_CONVERSION_",
  "APP_INSTALL_",
  "APP_REINSTALL_",
  "APP_LAUNCH_",
  "APP_PURCHASE_",
  "APP_SIGN_UP_",
  "APP_ADD_",
  "APP_VIEW_",
  "APP_LEVEL_",
  "APP_COMPLETED_",
  "APP_SPEND_",
  "APP_SEARCH_",
  "APP_REDOWNLOAD_",
  "ROAS",
];

function isConversionField(field: string): boolean {
  for (const prefix of CONVERSION_FIELD_PREFIXES) {
    if (field === prefix) return true;
    if (field.startsWith(prefix)) return true;
  }
  return false;
}

// ============================================================================
// Build a single sync report request
// ============================================================================

/**
 * Builds a `POST /ad_accounts/{id}/reports` request from a typed options shape.
 *
 * Validation order (fail-fast, with synchronous throws so tests can assert
 * specific Error messages — no async error envelopes for build-time errors):
 *   1. Breakdown count ≤ 3 (or ≤ 4 for COUNTRY+REGION special case)
 *   2. Date range ≤ 90 days
 *   3. HOUR breakdown ⇒ date range ≤ 7 days
 *   4. Strip deprecated metric fields (with warnings)
 *   5. Strip conversion fields when GALLERY_ITEM_ID is the active breakdown
 *   6. Clamp REACH/FREQUENCY pre-2024-06-01 (with warning)
 *   7. Normalize alias breakdowns to canonical
 *   8. Encode ISO 8601 timestamps at hour boundaries
 *
 * NOTE: `fields` validation against the catalogue's `apiField` allowlist is
 * the caller's responsibility — this function trusts the input string list
 * (it's the catalogue type-system enforcement, not request-build's job).
 */
export function buildRedditReportRequest(
  adAccountId: string,
  options: RedditAdsReportRequestOptions
): RedditAdsReportRequest {
  if (typeof adAccountId !== "string" || adAccountId.length === 0) {
    throw new Error("Reddit Ads adAccountId is required");
  }

  // Normalize aliases first so subsequent checks see the canonical names.
  const normalizedBreakdowns: RedditAdsBreakdown[] = options.breakdowns.map(
    normalizeBreakdown
  );

  // 1. Breakdown count enforcement
  if (normalizedBreakdowns.length > REDDIT_ADS_MAX_BREAKDOWNS_PER_REQUEST) {
    if (
      normalizedBreakdowns.length > REDDIT_ADS_MAX_BREAKDOWNS_WITH_COUNTRY_REGION ||
      !isCountryRegionSpecialCase(normalizedBreakdowns)
    ) {
      throw new Error(
        `Reddit Ads reports allow max ${REDDIT_ADS_MAX_BREAKDOWNS_PER_REQUEST} breakdowns per request, or ${REDDIT_ADS_MAX_BREAKDOWNS_WITH_COUNTRY_REGION} when combining COUNTRY+REGION; got ${normalizedBreakdowns.length}`
      );
    }
  }

  // 2. Date range validation
  if (
    !(options.startsAt instanceof Date) ||
    !(options.endsAt instanceof Date) ||
    Number.isNaN(options.startsAt.getTime()) ||
    Number.isNaN(options.endsAt.getTime())
  ) {
    throw new Error("Reddit Ads startsAt and endsAt must be valid Date objects");
  }
  if (options.startsAt.getTime() > options.endsAt.getTime()) {
    throw new Error("Reddit Ads startsAt must be on or before endsAt");
  }

  const rangeDays = daysBetween(options.startsAt, options.endsAt) + 1;
  if (rangeDays > REDDIT_ADS_MAX_FETCHMETRICS_DAYS) {
    throw new Error(
      `Reddit Ads reports cap fetch ranges at ${REDDIT_ADS_MAX_FETCHMETRICS_DAYS} days; got ${rangeDays}`
    );
  }

  // 3. HOUR breakdown gating
  if (
    normalizedBreakdowns.includes("HOUR") &&
    rangeDays > REDDIT_ADS_MAX_HOUR_BREAKDOWN_DAYS
  ) {
    throw new Error(
      `Reddit Ads HOUR breakdown requires date range ≤ ${REDDIT_ADS_MAX_HOUR_BREAKDOWN_DAYS} days; got ${rangeDays}`
    );
  }

  if (typeof options.timeZoneId !== "string" || options.timeZoneId.length === 0) {
    throw new Error("Reddit Ads time_zone_id is required");
  }

  const warnings: RedditAdsReportRequestWarning[] = [];

  // 4. Strip deprecated metric fields
  const deprecatedSet = new Set<string>(REDDIT_ADS_DEPRECATED_METRIC_FIELDS);
  let workingFields: string[] = [];
  for (const f of options.fields) {
    if (deprecatedSet.has(f)) {
      warnings.push({
        reason: `Stripped deprecated wire field "${f}" — this is a DIMENSION in v3, use breakdowns instead`,
      });
      continue;
    }
    workingFields.push(f);
  }

  // 5. Strip conversion fields when GALLERY_ITEM_ID is the active breakdown
  if (normalizedBreakdowns.includes("GALLERY_ITEM_ID")) {
    const before = workingFields.length;
    workingFields = workingFields.filter((f) => !isConversionField(f));
    const stripped = before - workingFields.length;
    if (stripped > 0) {
      warnings.push({
        reason: `GALLERY_ITEM_ID breakdown does not return conversion data — stripped ${stripped} conversion field(s) from the request`,
      });
    }
  }

  // 6. Clamp REACH/FREQUENCY to 2024-06-01 floor
  const fieldsHaveReach = workingFields.includes("REACH");
  const fieldsHaveFrequency = workingFields.includes("FREQUENCY");
  let effectiveStartsAt = options.startsAt;
  if (
    (fieldsHaveReach || fieldsHaveFrequency) &&
    options.startsAt.getTime() < REDDIT_ADS_REACH_FREQ_AVAILABLE_FROM.getTime()
  ) {
    effectiveStartsAt = REDDIT_ADS_REACH_FREQ_AVAILABLE_FROM;
    warnings.push({
      reason: `REACH/FREQUENCY only available from 2024-06-01 — clamped starts_at to 2024-06-01T00:00:00Z`,
    });
  }

  // 8. Encode ISO 8601 timestamps
  const starts_at = encodeRedditTimestamp(effectiveStartsAt);
  const ends_at = encodeRedditTimestamp(options.endsAt);

  const requestBody: RedditAdsReportRequest["body"] = {
    data: {
      starts_at,
      ends_at,
      fields: workingFields,
      breakdowns: normalizedBreakdowns,
      time_zone_id: options.timeZoneId,
      ...(options.clickAttributionWindow !== undefined
        ? { click_attribution_window: options.clickAttributionWindow }
        : {}),
      ...(options.viewAttributionWindow !== undefined
        ? { view_attribution_window: options.viewAttributionWindow }
        : {}),
    },
  };

  return {
    url: REDDIT_ADS_REPORTS_ENDPOINT(adAccountId),
    body: requestBody,
    warnings,
  };
}

// ============================================================================
// Build a chunked sub-request (for client-side temporal partitioning)
// ============================================================================

/**
 * Compute the half-open `[chunkStart, chunkEnd)` window for one chunk in a
 * temporally partitioned async fan-out.
 *
 * The last chunk's end is the original `endsAt` (inclusive boundary on the
 * final window only); intermediate chunks use half-open intervals to avoid
 * boundary-day double-count when adjacent chunks are merged.
 *
 * Example for a 90-day range with chunkDays=30:
 *   chunk 0 → [start, start+30d)
 *   chunk 1 → [start+30d, start+60d)
 *   chunk 2 → [start+60d, end]   ← inclusive end on the last chunk
 */
export function buildRedditReportRequestForAsyncChunk(
  adAccountId: string,
  baseOptions: RedditAdsReportRequestOptions,
  chunkIndex: number,
  totalChunks: number,
  chunkDays: number = REDDIT_ADS_MAX_ASYNC_CHUNK_DAYS
): RedditAdsReportRequest {
  if (chunkIndex < 0 || chunkIndex >= totalChunks) {
    throw new Error(
      `chunkIndex ${chunkIndex} out of range [0, ${totalChunks})`
    );
  }

  const startMs = baseOptions.startsAt.getTime();
  const endMs = baseOptions.endsAt.getTime();
  const dayMs = 24 * 60 * 60 * 1000;

  const chunkStartMs = startMs + chunkIndex * chunkDays * dayMs;
  let chunkEndMs: number;
  if (chunkIndex === totalChunks - 1) {
    // Last chunk: inclusive end at the original endsAt.
    chunkEndMs = endMs;
  } else {
    // Half-open: subtract one day from the next chunk's start so the boundary
    // day belongs to exactly one chunk.
    chunkEndMs = startMs + (chunkIndex + 1) * chunkDays * dayMs - dayMs;
  }

  if (chunkEndMs > endMs) chunkEndMs = endMs;
  if (chunkEndMs < chunkStartMs) chunkEndMs = chunkStartMs;

  return buildRedditReportRequest(adAccountId, {
    ...baseOptions,
    startsAt: new Date(chunkStartMs),
    endsAt: new Date(chunkEndMs),
  });
}

/**
 * Compute the chunk count for a date range and per-chunk day budget.
 * Used by `runRedditAdsAsyncChunked` to pre-size the chunk array.
 */
export function computeRedditAsyncChunkCount(
  startsAt: Date,
  endsAt: Date,
  chunkDays: number = REDDIT_ADS_MAX_ASYNC_CHUNK_DAYS
): number {
  const days = daysBetween(startsAt, endsAt) + 1;
  return Math.max(1, Math.ceil(days / chunkDays));
}
